// deviceSecret — the ONE place that decides how the per-device secret is
// turned into bytes. Spec: docs/specs/device-and-worker-sign-in.md §5. Phase 4e.
//
// ⚠️⚠️ THIS FILE EXISTS BECAUSE THE ENCODING WAS ALREADY WRONG.
//
// `/enrol` mints `hmac_secret` with the edge function's `mintSecret()`, which
// returns 32 random bytes as **64 lowercase hex characters**. Both client
// modules that consumed it — deviceOfflineCache (HKDF) and deviceCaptureQueue
// (HMAC) — decoded it with `atob()`, i.e. as BASE64.
//
// That silently "works" in isolation: the hex alphabet is a subset of the
// base64 alphabet, so `atob` succeeds and returns 48 bytes of a different
// number. The cache never noticed because it encrypts and decrypts with the
// same wrong key. The QUEUE would have noticed catastrophically — the server
// derives the key from the same secret, and if the two sides decode it
// differently then EVERY offline capture fails signature verification, for
// every device, forever, with nothing in the UI to say why. The queue's own
// header calls this exact hazard out; it was live all the same.
//
// So: one decoder, hex, validated, used by both sides. The server's copy in
// supabase/functions/_shared/deviceDrain.ts must agree with it, and the golden
// vector in captureSignatureGolden.json is what proves they do.

/**
 * 64 hex characters → 32 bytes.
 *
 * Returns null rather than throwing, and null means "this device cannot do
 * offline" — never "use a default". There is no safe fallback key: a fixed one
 * would sign every tenant's captures with the same secret.
 */
export function deviceSecretBytes(secret: string | null | undefined): Uint8Array | null {
  if (!secret) return null
  const s = secret.trim().toLowerCase()
  // Length must be even and the alphabet must be hex. A secret that fails this
  // is not a secret we minted, and guessing at its encoding is how the base64
  // mistake happened in the first place.
  if (s.length === 0 || s.length % 2 !== 0) return null
  if (!/^[0-9a-f]+$/.test(s)) return null
  const out = new Uint8Array(s.length / 2)
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16)
  }
  return out
}

/** Bytes → lowercase hex. Used for signatures and digests on the wire. */
export function bytesToHex(bytes: Uint8Array): string {
  let hex = ''
  for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, '0')
  return hex
}
